/**
 * Bundle Builder - Collection switching and drawer toggle
 */
// Custom Element: Variant Dropdown
class VariantDropdown extends HTMLElement {
  constructor() {
    super();
    this.trigger = null;
    this.menu = null;
    this.label = null;
    this.selectedVariantId = null;
  }

  connectedCallback() {
    this.trigger = this.querySelector('.bundle-builder__dropdown-trigger');
    this.menu = this.querySelector('.bundle-builder__dropdown-menu');
    this.label = this.querySelector('.bundle-builder__dropdown-label');

    if (!this.trigger || !this.menu) return;

    // Toggle dropdown on trigger click
    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
    });

    // Handle option selection
    this.menu.addEventListener('click', (e) => {
      const option = e.target.closest('.bundle-builder__dropdown-option');
      if (option) {
        this.selectOption(option);
      }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.close();
      }
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close();
      }
    });
  }

  toggle() {
    if (this.menu.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.menu.hidden = false;
    this.classList.add('open');
  }

  close() {
    this.menu.hidden = true;
    this.classList.remove('open');
  }

  selectOption(option) {
    const variantId = option.dataset.variantId;
    const variantTitle = option.dataset.title;
    const variantPrice = option.dataset.price;

    // Update label
    if (this.label) {
      this.label.textContent = variantTitle;
    }

    // Mark as selected
    this.querySelectorAll('.bundle-builder__dropdown-option').forEach(opt => {
      opt.classList.remove('selected');
    });
    option.classList.add('selected');

    // Store selected variant
    this.selectedVariantId = variantId;

    // Update the Add button
    const productId = this.dataset.productId;
    const container = this.closest('.bundle-builder');
    if (container && productId) {
      const addBtn = container.querySelector(`.bundle-builder__add-btn[data-product-id="${productId}"]`);
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.classList.remove('disabled');
        addBtn.dataset.variantId = variantId;
        addBtn.dataset.price = variantPrice;
      }
    }

    // Close dropdown
    this.close();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('variant-selected', {
      bubbles: true,
      detail: {
        variantId,
        variantTitle,
        variantPrice,
        productId: this.dataset.productId
      }
    }));
  }
}

// Register custom element
if (!customElements.get('variant-dropdown')) {
  customElements.define('variant-dropdown', VariantDropdown);
}

document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('.bundle-builder');
  if (!container) return;

  // Elements
  const filterBtns = container.querySelectorAll('.bundle-builder__filter-btn');
  const productsContainer = container.querySelector('.bundle-builder__products-container');
  const loadingEl = container.querySelector('.bundle-builder__loading');
  const cartSummary = container.querySelector('.bundle-builder__cart-summary');
  const cartContent = container.querySelector('.bundle-builder__cart-content');
  const expandedDrawer = container.querySelector('.bundle-builder__expanded-drawer');
  const itemsToggle = container.querySelector('.bundle-builder__items-toggle');
  const keepShoppingBtn = container.querySelector('.bundle-builder__keep-shopping');

  // Get currency rate for conversion
  const currencyRate = parseFloat(window.Shopify?.currency?.rate) || 1;

  // Get max threshold for progress calculation (converted by currency rate)
  const maxThreshold = (parseFloat(cartSummary?.dataset.maxThreshold) || 150) * currencyRate;

  // Get discount tiers (thresholds converted by currency rate)
  const tierElements = container.querySelectorAll('.bundle-builder__tier');
  const tiers = Array.from(tierElements).map(el => ({
    threshold: parseFloat(el.dataset.threshold) * currencyRate,
    discount: parseFloat(el.dataset.discount),
    element: el
  })).sort((a, b) => a.threshold - b.threshold);

  // Update tier labels with converted currency
  const currencySymbol = window.Shopify?.currency?.active || '';
  tierElements.forEach(el => {
    const label = el.querySelector('.bundle-builder__tier-label');
    if (label) {
      const baseThreshold = parseFloat(label.dataset.baseThreshold) || 0;
      const convertedThreshold = Math.round(baseThreshold * currencyRate);
      label.textContent = `Spend $${convertedThreshold}`;
    }
  });

  // Bundle cart state
  const bundleCart = {
    items: [], // { id, variantId, title, price, image, quantity, variantTitle }

    addItem(productId, variantId, title, price, image, variantTitle = '') {
      const existingItem = this.items.find(item => item.variantId === variantId);
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        this.items.push({
          id: productId,
          variantId,
          title,
          price: parseFloat(price),
          image,
          quantity: 1,
          variantTitle
        });
      }
      this.updateUI();
    },

    removeItem(variantId) {
      this.items = this.items.filter(item => item.variantId !== variantId);
      this.updateUI();
    },

    updateQuantity(variantId, delta) {
      const item = this.items.find(item => item.variantId === variantId);
      if (item) {
        item.quantity = Math.max(1, item.quantity + delta);
        this.updateUI();
      }
    },

    getSubtotal() {
      return this.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    },

    getTotalItems() {
      return this.items.reduce((sum, item) => sum + item.quantity, 0);
    },

    getCurrentTier() {
      const subtotal = this.getSubtotal() / 100; // Convert cents to dollars
      let currentTier = null;
      for (const tier of tiers) {
        if (subtotal >= tier.threshold) {
          currentTier = tier;
        }
      }
      return currentTier;
    },

    getNextTier() {
      const subtotal = this.getSubtotal() / 100;
      for (const tier of tiers) {
        if (subtotal < tier.threshold) {
          return tier;
        }
      }
      return null;
    },

    getDiscount() {
      const currentTier = this.getCurrentTier();
      if (currentTier) {
        return this.getSubtotal() * (currentTier.discount / 100);
      }
      return 0;
    },

    updateUI() {
      const subtotal = this.getSubtotal();
      const subtotalDollars = subtotal / 100;
      const totalItems = this.getTotalItems();
      const currentTier = this.getCurrentTier();
      const nextTier = this.getNextTier();
      const discount = this.getDiscount();
      const total = subtotal - discount;

      // Update progress bar - each tier gets equal segment, progress within tier is proportional
      let progressPercent = 0;
      const tierCount = tiers.length;

      if (tierCount === 0 || subtotalDollars === 0) {
        // No tiers or zero amount, progress is 0%
        progressPercent = 0;
      } else {
        const segmentSize = 100 / tierCount; // Each tier segment percentage

        // Find which tier we're currently in
        let currentTierIndex = -1; // -1 means not reached first tier yet
        for (let i = 0; i < tiers.length; i++) {
          if (subtotalDollars >= tiers[i].threshold) {
            currentTierIndex = i;
          } else {
            break;
          }
        }

        if (currentTierIndex === tierCount - 1) {
          // Reached last tier, progress is 100%
          progressPercent = 100;
        } else {
          // Calculate progress within current segment
          const nextTierIndex = currentTierIndex + 1;
          const prevThreshold = currentTierIndex >= 0 ? tiers[currentTierIndex].threshold : 0;
          const nextThreshold = tiers[nextTierIndex].threshold;

          // Progress within current segment (0 to 1)
          const segmentProgress = (subtotalDollars - prevThreshold) / (nextThreshold - prevThreshold);

          // Total progress = completed segments + current segment progress
          const completedSegments = currentTierIndex + 1; // -1 + 1 = 0 if not reached first tier
          progressPercent = (completedSegments * segmentSize) + (segmentProgress * segmentSize);
        }
      }

      progressPercent = Math.min(Math.max(progressPercent, 0), 100);

      const progressFills = container.querySelectorAll('.bundle-builder__progress-fill');
      progressFills.forEach(fill => {
        fill.style.width = `${progressPercent}%`;
      });

      // Update tier badges (use converted thresholds from tiers array)
      tiers.forEach((tier, index) => {
        const badge = tier.element.querySelector('.bundle-builder__tier-badge');
        if (badge) {
          if (subtotalDollars >= tier.threshold) {
            badge.classList.add('unlocked');
          } else {
            badge.classList.remove('unlocked');
          }
        }
      });

      // Update items count - keep "View Bundle" text static
      // const itemsCountEl = container.querySelector('.bundle-builder__items-count');
      // if (itemsCountEl) {
      //   itemsCountEl.textContent = `${totalItems} Item${totalItems !== 1 ? 's' : ''}`;
      // }

      // Update spend message
      const spendMessage = container.querySelector('.bundle-builder__spend-message');
      if (spendMessage && nextTier) {
        const remaining = nextTier.threshold - subtotalDollars;
        spendMessage.innerHTML = `Spend <strong class="bundle-builder__remaining-amount">$${remaining.toFixed(2)}</strong> more to unlock <strong>${nextTier.discount}% OFF</strong>`;
      } else if (spendMessage && currentTier) {
        spendMessage.innerHTML = `You've unlocked <strong>${currentTier.discount}% OFF</strong>!`;
      }

      // Update drawer spend message
      const drawerSpendMessage = container.querySelector('.bundle-builder__drawer-spend-message');
      if (drawerSpendMessage && nextTier) {
        const remaining = nextTier.threshold - subtotalDollars;
        drawerSpendMessage.innerHTML = `Spend <strong class="bundle-builder__drawer-remaining">$${remaining.toFixed(2)}</strong> more to unlock a <strong class="bundle-builder__drawer-discount">${nextTier.discount}%</strong> discount.`;
      } else if (drawerSpendMessage && currentTier) {
        drawerSpendMessage.innerHTML = `You've unlocked a <strong class="bundle-builder__drawer-discount">${currentTier.discount}%</strong> discount!`;
      }

      // Update checkout buttons
      const checkoutBtns = container.querySelectorAll('.bundle-builder__checkout-btn');
      checkoutBtns.forEach(btn => {
        btn.disabled = totalItems === 0;
        const priceEl = btn.querySelector('.bundle-builder__checkout-price');
        const originalEl = btn.querySelector('.bundle-builder__checkout-original');
        if (priceEl) {
          priceEl.textContent = `$${(total / 100).toFixed(2)}`;
        }
        if (originalEl) {
          if (discount > 0) {
            originalEl.textContent = `$${(subtotal / 100).toFixed(2)}`;
            originalEl.style.display = '';
          } else {
            originalEl.style.display = 'none';
          }
        }
      });

      // Update drawer items list
      const drawerItemsList = container.querySelector('.bundle-builder__drawer-items');
      if (drawerItemsList) {
        const discountPercent = currentTier ? currentTier.discount : 0;
        drawerItemsList.innerHTML = this.items.map(item => {
          const itemTotal = (item.price * item.quantity) / 100;
          const discountedPrice = itemTotal * (1 - discountPercent / 100);
          const hasDiscount = discountPercent > 0;

          return `
          <div class="bundle-builder__drawer-item" data-variant-id="${item.variantId}">
            <img class="bundle-builder__drawer-item-image" src="${item.image}" alt="${item.title}">
            <div class="bundle-builder__drawer-item-content">
              <h4 class="bundle-builder__drawer-item-title">${item.title}${item.variantTitle ? ` - ${item.variantTitle}` : ''}</h4>
              <div class="bundle-builder__drawer-item-actions">
                <div class="bundle-builder__quantity-selector">
                  <button class="bundle-builder__quantity-btn" data-action="decrease" data-variant-id="${item.variantId}">−</button>
                  <span class="bundle-builder__quantity-value">${item.quantity}</span>
                  <button class="bundle-builder__quantity-btn" data-action="increase" data-variant-id="${item.variantId}">+</button>
                </div>
              </div>
            </div>
            <div class="bundle-builder__drawer-item-right">
              <div class="bundle-builder__drawer-item-prices">
                <span class="bundle-builder__drawer-item-price">$${hasDiscount ? discountedPrice.toFixed(2) : itemTotal.toFixed(2)} ${currencySymbol}</span>
                ${hasDiscount ? `<span class="bundle-builder__drawer-item-original">$${itemTotal.toFixed(2)} ${currencySymbol}</span>` : ''}
              </div>
              <button class="bundle-builder__drawer-item-remove" data-variant-id="${item.variantId}">
                <svg aria-hidden="true" focusable="false" class="icon icon-trash" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M5.5 5.5V12.5M8 5.5V12.5M10.5 5.5V12.5M2.5 3.5H13.5M12 3.5L11.5 14H4.5L4 3.5M6 3.5V2C6 1.72386 6.22386 1.5 6.5 1.5H9.5C9.77614 1.5 10 1.72386 10 2V3.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        `}).join('');
      }

      // Update summary
      const subtotalEl = container.querySelector('.bundle-builder__subtotal-value');
      const savingEl = container.querySelector('.bundle-builder__saving-value');
      const totalEl = container.querySelector('.bundle-builder__total-value');

      if (subtotalEl) subtotalEl.textContent = `$${(subtotal / 100).toFixed(2)}`;
      if (savingEl) savingEl.textContent = `$${(discount / 100).toFixed(2)}`;
      if (totalEl) totalEl.textContent = `$${(total / 100).toFixed(2)}`;

      // Show/hide free gift based on tier (convert threshold with currency rate)
      const freeGiftItems = container.querySelectorAll('.bundle-builder__free-gift-item');
      freeGiftItems.forEach(giftItem => {
        const giftThreshold = parseFloat(giftItem.dataset.tierThreshold) * currencyRate;
        if (subtotalDollars >= giftThreshold) {
          giftItem.hidden = false;
        } else {
          giftItem.hidden = true;
        }
      });
    }
  };

  // Handle Add button clicks
  container.addEventListener('click', (e) => {
    const addBtn = e.target.closest('.bundle-builder__add-btn');
    if (addBtn && !addBtn.disabled) {
      const productId = addBtn.dataset.productId;
      const variantId = addBtn.dataset.variantId;
      const price = addBtn.dataset.price;
      const title = addBtn.dataset.title;
      const image = addBtn.dataset.image;

      // Get variant title if dropdown exists
      const dropdown = container.querySelector(`variant-dropdown[data-product-id="${productId}"]`);
      const variantTitle = dropdown?.querySelector('.bundle-builder__dropdown-label')?.textContent || '';

      bundleCart.addItem(productId, variantId, title, price, image, variantTitle !== 'Choose your shade' ? variantTitle : '');

      // Visual feedback
      addBtn.classList.add('added');
      addBtn.textContent = 'ADDED ✓';
      setTimeout(() => {
        addBtn.classList.remove('added');
        addBtn.textContent = 'ADD +';
      }, 1000);
    }
  });

  // Handle quantity and remove in drawer
  container.addEventListener('click', (e) => {
    const quantityBtn = e.target.closest('.bundle-builder__quantity-btn');
    if (quantityBtn) {
      const variantId = quantityBtn.dataset.variantId;
      const action = quantityBtn.dataset.action;
      if (action === 'increase') {
        bundleCart.updateQuantity(variantId, 1);
      } else if (action === 'decrease') {
        bundleCart.updateQuantity(variantId, -1);
      }
    }

    const removeBtn = e.target.closest('.bundle-builder__drawer-item-remove');
    if (removeBtn) {
      const variantId = removeBtn.dataset.variantId;
      bundleCart.removeItem(variantId);
    }

    // Handle Add to Cart button
    const checkoutBtn = e.target.closest('.bundle-builder__checkout-btn');
    if (checkoutBtn && !checkoutBtn.disabled) {
      addBundleToCart();
    }
  });

  // Add all bundle items to Shopify cart
  async function addBundleToCart() {
    if (bundleCart.items.length === 0) return;

    const checkoutBtns = container.querySelectorAll('.bundle-builder__checkout-btn');
    checkoutBtns.forEach(btn => {
      btn.disabled = true;
      btn.querySelector('.bundle-builder__checkout-text').textContent = 'ADDING...';
    });

    try {
      // Prepare items for cart
      const items = bundleCart.items.map(item => ({
        id: parseInt(item.variantId),
        quantity: item.quantity
      }));

      // Free gift is for display only, not added to cart

      // Add to cart via Shopify AJAX API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Success - clear bundle and open cart
      bundleCart.items = [];
      bundleCart.updateUI();

      // Close drawer if open
      closeDrawer();

      // Open mini cart using fetchCart from mini-cart.js
      if (typeof fetchCart === 'function') {
        fetchCart();
      }

      // Reset button text
      checkoutBtns.forEach(btn => {
        btn.querySelector('.bundle-builder__checkout-text').textContent = 'ADD BUNDLE TO CART';
      });

    } catch (error) {
      console.error('Error adding to cart:', error);
      // Reset button
      checkoutBtns.forEach(btn => {
        btn.disabled = false;
        btn.querySelector('.bundle-builder__checkout-text').textContent = 'ADD BUNDLE TO CART';
      });
      alert('Failed to add items to cart. Please try again.');
    }
  }

  // Product type filter switching (client-side filtering)
  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      // Update active state
      filterBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      const productType = btn.dataset.productType;
      if (!productType) return;

      // Get all product cards
      const productCards = productsContainer.querySelectorAll('.bundle-builder__product-card');

      productCards.forEach(card => {
        if (productType === 'all') {
          // Show all products
          card.hidden = false;
        } else {
          // Filter by product type
          const cardType = card.dataset.productType;
          card.hidden = cardType !== productType;
        }
      });
    });
  });

  // Toggle expanded drawer
  const overlay = container.querySelector('.bundle-builder__overlay');

  function openDrawer() {
    if (cartSummary && expandedDrawer) {
      cartSummary.classList.add('expanded');
      container.classList.add('has-expanded-drawer');
      cartContent.hidden = true;
      expandedDrawer.hidden = false;
      document.body.style.overflow = 'hidden';
    }
  }

  function closeDrawer() {
    if (cartSummary && expandedDrawer) {
      cartSummary.classList.remove('expanded');
      container.classList.remove('has-expanded-drawer');
      cartContent.hidden = false;
      expandedDrawer.hidden = true;
      document.body.style.overflow = '';
    }
  }

  // Close drawer when clicking overlay
  if (overlay) {
    overlay.addEventListener('click', () => {
      closeDrawer();
    });
  }

  // Items toggle button - opens drawer
  if (itemsToggle) {
    itemsToggle.addEventListener('click', () => {
      openDrawer();
    });
  }

  // Keep Shopping button - closes drawer
  if (keepShoppingBtn) {
    keepShoppingBtn.addEventListener('click', () => {
      closeDrawer();
    });
  }

  // Close drawer when clicking outside
  if (expandedDrawer) {
    expandedDrawer.addEventListener('click', (e) => {
      if (e.target === expandedDrawer) {
        closeDrawer();
      }
    });
  }

  // Close drawer on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && expandedDrawer && !expandedDrawer.hidden) {
      closeDrawer();
    }
  });

  // Desktop: Detect when cart summary reaches bottom of viewport
  function checkCartPosition() {
    if (!cartSummary || window.innerWidth < 990) return;

    const rect = cartSummary.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const isAtBottom = rect.bottom >= viewportHeight - 20;

    if (isAtBottom) {
      cartSummary.classList.add('is-at-bottom');
    } else {
      cartSummary.classList.remove('is-at-bottom');
    }
  }

  // Check position on scroll and resize
  window.addEventListener('scroll', checkCartPosition, { passive: true });
  window.addEventListener('resize', checkCartPosition, { passive: true });
  checkCartPosition();

  // Initialize UI
  bundleCart.updateUI();
});
